import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, Search } from "lucide-react";
import { api, media } from "../lib/api";
import { useCart, useSite } from "../lib/store";
import { CatIcon, Daisy } from "../components/Daisy";
import { ProductCard } from "../components/ProductCard";
import { useSeo } from "../components/Seo";

const SORTS = [["order", "Doporučené"], ["new", "Nejnovější"], ["price_asc", "Nejlevnější"], ["price_desc", "Nejdražší"], ["name", "Abecedně"]];

export const PageHead = ({ title, sub, image }) => (
  <div className="relative overflow-hidden bg-[#E9EFE0]">
    <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-12 sm:px-8">
      <div className="flex-1">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="h-display text-4xl sm:text-5xl" data-testid="page-title">{title}</motion.h1>
        {sub && <p className="mt-3 max-w-xl text-[#2D3426]/80">{sub}</p>}
      </div>
      {image ? <img src={media(image)} alt="" className="hidden h-28 w-28 rounded-full border-4 border-white object-cover shadow md:block" /> : <Daisy size={90} className="animate-spin-slow hidden md:block" />}
    </div>
  </div>
);

export default function Shop() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const { site } = useSite();
  const [list, setList] = useState(null);
  const q = sp.get("q") || "";
  const sort = sp.get("sort") || "order";
  const sale = sp.get("sale") === "1";
  const [qi, setQi] = useState(q);
  const cats = site?.categories || [];
  const cat = cats.find((c) => c.slug === slug);
  useSeo({ title: cat ? cat.name : "E-shop", description: cat?.description || undefined });
  useEffect(() => {
    setList(null);
    api.get("/products", { params: { category: slug, q: q || undefined, sort, sale: sale || undefined } }).then((r) => setList(r.data));
  }, [slug, q, sort, sale]);
  useEffect(() => setQi(q), [q]);
  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n);
  };
  return (
    <div data-testid="shop-page">
      <PageHead title={cat ? cat.name : q ? `Hledání: ${q}` : "E-shop"} sub={cat?.description || "Hravé PDF materiály ke stažení ihned po zaplacení."} image={cat?.image} />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-2">
          <Link to="/eshop" className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${!slug ? "border-[#7A8460] bg-[#7A8460] text-white" : "bg-white hover:bg-[#E9EFE0]"}`} data-testid="filter-all">Vše</Link>
          {cats.map((c) => (
            <Link key={c.id} to={`/kategorie/${c.slug}`} data-testid={`filter-${c.slug}`}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${slug === c.slug ? "border-[#7A8460] bg-[#7A8460] text-white" : "bg-white hover:bg-[#E9EFE0]"}`}>
              <CatIcon name={c.icon} className="h-4 w-4" /> {c.name}
            </Link>
          ))}
          <Link to="/darkove-poukazy" data-testid="filter-darkove-poukazy" className="flex shrink-0 items-center gap-2 rounded-full border border-[#F2C230] bg-[#FDF3D3] px-4 py-2 text-sm transition-colors hover:bg-[#F2C230]">
            <Gift className="h-4 w-4" /> Dárkové poukazy
          </Link>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <form onSubmit={(e) => { e.preventDefault(); setParam("q", qi); }} className="flex flex-1 items-center gap-2 rounded-full border bg-white px-4 py-2.5 sm:max-w-xs">
            <Search className="h-4 w-4 text-[#6E7565]" />
            <input value={qi} onChange={(e) => setQi(e.target.value)} placeholder="Hledat v e-shopu" className="flex-1 bg-transparent text-sm outline-none" data-testid="shop-search-input" />
          </form>
          <label className="flex items-center gap-2 rounded-full border bg-white px-4 py-2.5 text-sm">
            <input type="checkbox" checked={sale} onChange={(e) => setParam("sale", e.target.checked ? "1" : "")} className="accent-[#7A8460]" data-testid="shop-sale-filter" /> Jen ve slevě
          </label>
          <select value={sort} onChange={(e) => setParam("sort", e.target.value)} className="ml-auto rounded-full border bg-white px-4 py-2.5 text-sm outline-none" data-testid="shop-sort-select">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {list === null ? (
          <div className="grid grid-cols-2 gap-4 pt-8 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, k) => <div key={k} className="h-72 animate-pulse rounded-3xl bg-white" />)}</div>
        ) : list.length ? (
          <div className="grid grid-cols-2 gap-4 pt-8 lg:grid-cols-4" data-testid="product-grid">{list.map((p, k) => <ProductCard key={p.id} p={p} i={k} />)}</div>
        ) : (
          <div className="py-20 text-center" data-testid="shop-empty"><Daisy size={80} className="mx-auto animate-wiggle" /><p className="h-section mt-4 text-2xl">Tady zatím nic nekvete</p><p className="text-[#6E7565]">Zkuste jinou kategorii nebo hledaný výraz.</p></div>
        )}
      </div>
    </div>
  );
}

export function Favorites() {
  const { favs } = useCart();
  useSeo({ title: "Oblíbené" });
  return (
    <div data-testid="favorites-page">
      <PageHead title="Oblíbené" sub="Materiály, které jste si označili srdíčkem." />
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-10 sm:px-8 lg:grid-cols-4">
        {favs.length ? favs.map((p, k) => <ProductCard key={p.id} p={p} i={k} />) : <p className="col-span-full py-12 text-center text-[#6E7565]">Zatím nemáte žádné oblíbené produkty.</p>}
      </div>
    </div>
  );
}
